import React, { useEffect, useState, useRef } from 'react';
import { makeStyles } from '@material-ui/styles';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { CloseIcon } from 'components';
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  CircularProgress
} from '@material-ui/core';
import { actions as reportActions } from 'store/reports';
import clsx from 'clsx';
import { Alert } from '@material-ui/lab';

const { getMessageHistory } = reportActions;

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3)
  },
  content: {
    marginTop: theme.spacing(2)
  },
  header: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  closeIcon: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: '0px'
  },
  iFrame: {
    width: '100%',
    minHeight: '400px',
    border: '0px',
    marginTop: theme.spacing(2)
  },
  labelDiv: {
    display: 'flex',
    margin: '8px 32px'
  },
  innerKeyDiv: {
    width: '20%',
    fontWeight: 500,
    overflowWrap: 'anywhere'
  },
  innerValueDiv: {
    width: '80%',
    overflowWrap: 'anywhere'
  },
  messageContainer: {
    display: 'flex',
    flexDirection: 'column',
    columnGap: theme.spacing(2)
  },
  messageContent: {
    display: 'flex',
    justifyContent: 'center',
    margin: '25%'
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    margin: '20%'
  }
}));

const EllipsisAddresses = ({ value }) => {
  const [expanded, setExpanded] = useState(false);
  const [showEllipsis, setShowEllipsis] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    if (textRef.current) {
      setShowEllipsis(
        textRef.current.scrollWidth > textRef.current.clientWidth
      );
    }
  }, [value, expanded]);
  if (!value || value === '-') return '-';
  if (expanded) {
    return (
      <div>
        {value}
        <span
          style={{
            color: '#1976d2',
            fontSize: 12,
            cursor: 'pointer',
            marginLeft: 8
          }}
          onClick={() => setExpanded(false)}
        >
          Show less
        </span>
      </div>
    );
  }
  return (
    <span style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
      <span
        ref={textRef}
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          flex: 1
        }}
        title={value}
      >
        {value}
      </span>
      {showEllipsis && (
        <span
          style={{
            color: '#1976d2',
            fontSize: 12,
            cursor: 'pointer',
            marginLeft: 4,
            flexShrink: 0
          }}
          onClick={() => setExpanded(true)}
        >
          ...more
        </span>
      )}
    </span>
  );
};

const MessageDetails = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  let { messageUuid } = useParams();
  const { isModal = false, messageId } = props;

  if (isModal) {
    messageUuid = messageId;
  }

  const messageHistoryData = useSelector(
    (store) => store.reports.messageHistory
  );
  const messageHistoryLoading = useSelector(
    (store) => store.reports.messageHistory.getting
  );
  const [modifiedContent, setModifiedContent] = useState({});
  const [contentType, setContentType] = useState(null);

  const navBack = () => {
    history.push('/events');
  };

  useEffect(() => {
    dispatch(getMessageHistory({ messageUuid: messageUuid }));
  }, []);

  useEffect(() => {
    if (messageHistoryData.data[0]?.originalRequest) {
      let mailData =
        messageHistoryData.data[0]?.originalRequest.emailDeliveryRequest;
      if (mailData) {
        setContentType('EMAIL');
        setModifiedContent((prevState) => {
          return {
            ...prevState,
            toAddresses: mailData.metadata?.toAddresses?.join(', ') || '',
            ccAddresses: mailData.metadata?.ccAddresses?.join(', ') || '',
            bccAddresses: mailData.metadata?.bccAddresses?.join(', ') || '',
            fromName: mailData.metadata?.fromName || '',
            fromAddress: mailData.metadata?.fromAddress || '',
            subject: mailData.metadata?.subject || '',
            html: sanitizeHtml(mailData.content?.messageBody || '')
          };
        });
      }
      let smsData =
        messageHistoryData.data[0]?.originalRequest.smsDeliveryRequest;
      if (smsData) {
        setContentType('SMS');
        setModifiedContent((prevState) => {
          return {
            ...prevState,
            toNumbers: smsData.metadata?.toNumbers?.join(', ') || '',
            text: smsData.content?.messageBody || ''
          };
        });
      }
      if (!smsData && !mailData) {
        setContentType(null);
        setModifiedContent((prevState) => {
          return {
            ...prevState,
            toAddresses: '-',
            ccAddresses: '-',
            bccAddresses: '-',
            fromName: '-',
            fromAddress: '-',
            subject: '-',
            html: '-',
            toNumbers: '-',
            text: '-'
          };
        });
      }
    }
  }, [messageHistoryData]);

  const sanitizeHtml = (html) => {
    // Remove script tags and it's attributes from the html content
    let sanitizedHtml = html.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
    // Remove all attributes from the anchor tags
    sanitizedHtml = sanitizedHtml.replace(/<a\b[^>]*>/gi, '<a>');
    return sanitizedHtml;
  };

  const cardContent = () => {
    return (
      <div>
        <div>
          {contentType === 'EMAIL' && (
            <div className={classes.labelDiv}>
              <div className={classes.innerKeyDiv}>{'Sent By'}</div>
              <div className={classes.innerValueDiv}>
                {modifiedContent.fromName ? modifiedContent.fromName : '-'}
              </div>
            </div>
          )}
          {contentType === 'EMAIL' && (
            <div className={classes.labelDiv}>
              <div className={classes.innerKeyDiv}>{'Sent From'}</div>
              <div className={classes.innerValueDiv}>
                {modifiedContent.fromAddress
                  ? modifiedContent.fromAddress
                  : '-'}
              </div>
            </div>
          )}
          {contentType === 'EMAIL' && (
            <div className={classes.labelDiv}>
              <div className={classes.innerKeyDiv}>{'Sent To'}</div>
              <div className={classes.innerValueDiv}>
                {modifiedContent.toAddresses
                  ? modifiedContent.toAddresses
                  : '-'}
              </div>
            </div>
          )}
          {contentType === 'SMS' && (
            <div className={classes.labelDiv}>
              <div className={classes.innerKeyDiv}>{'Sent To'}</div>
              <div className={classes.innerValueDiv}>
                {modifiedContent.toNumbers ? modifiedContent.toNumbers : '-'}
              </div>
            </div>
          )}
          {contentType === 'EMAIL' && (
            <div className={classes.labelDiv}>
              <div className={classes.innerKeyDiv}>{'Subject'}</div>
              <div className={classes.innerValueDiv}>
                {modifiedContent.subject ? modifiedContent.subject : '-'}
              </div>
            </div>
          )}
        </div>
        {contentType === 'EMAIL' && (
          <div className={clsx(classes.labelDiv, classes.messageContainer)}>
            <div className={classes.innerKeyDiv}>{'Message Content'}</div>
            <iframe className={classes.iFrame} srcDoc={modifiedContent.html} />
          </div>
        )}
        {contentType === 'SMS' && (
          <div className={clsx(classes.labelDiv, classes.messageContainer)}>
            <div className={classes.innerKeyDiv}>{'Message Content'}</div>
            <div className={classes.iFrame} style={{ wordBreak: 'break-word' }}>
              {modifiedContent.text}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        {!isModal && (
          <CloseIcon className={classes.closeIcon} onClickClose={navBack} />
        )}
      </div>
      {messageHistoryLoading && (
        <div className={classes.loader}>
          <CircularProgress />
        </div>
      )}
      {!messageHistoryLoading &&
        (contentType ? (
          <div className={classes.content}>
            {!isModal ? (
              <Card>
                <CardHeader
                  title="Message Details"
                  titleTypographyProps={{ variant: 'h3' }}
                />
                <Divider />
                <CardContent>{cardContent()}</CardContent>
              </Card>
            ) : (
              <div>{cardContent()}</div>
            )}
          </div>
        ) : !isModal ? (
          <Alert severity="info">Message details not available</Alert>
        ) : (
          <div>Message details not available</div>
        ))}
    </div>
  );
};

export default MessageDetails;
